<script lang="ts">
    import { createEventDispatcher, onMount } from "svelte";
    import { Autocomplete, getAutocomplete } from "../helpers/api";
    import { getProviderIcon } from "../helpers/provider";
    import { SearchLine, CloseLine, Filter2Line, Filter2Fill } from "svelte-remixicon";
    import {_} from "svelte-i18n";

    const dispatch = createEventDispatcher();

    export let searchStringBack = '';
    export let mobileResult = false;

    let input: HTMLInputElement;
    let filterOn: boolean;
    let searchString: string = '';
    let autocomplete: Autocomplete[] = [];
    let selectedResult: string = '';

    let filter: string = 'all';
    let searchStatus = false;

    $: {
        if (searchStringBack != '') {
            searchString = searchStringBack;
            dispatch('back');
            getAutocompletion();
            if (window.outerWidth > 768) {
                input.focus();
            }
        }

        if (searchString != '') {
            dispatch('search', true);
        } else {
            dispatch('search', false);
        }
    }

    onMount(async () => {
        if (input.id == 'input') {
            input.focus();
        }
    });

    function getAutocompletion() {
        if (searchString != '') {
            getAutocomplete(searchString, filter, $_('code')).then((res) => {
                if (res.length > 0) {
                    autocomplete = res;
                }  else {
                    autocomplete = [];
                }
                history.pushState({}, null, "/search");
            });
        } else {
            autocomplete = [];
        }
    }

    function selectResult(id: string, select: boolean) {
        if (id != '') {
            selectedResult = id;
            if (select) {
                dispatch('result', {
                    id: id,
                    search: searchString,
                    filter: filter
                });
                autocomplete = [];
                selectedResult = '';
            }
        } else {
            selectedResult = '';
        }
    }

    function selectResultKey(direction: string) {
        if (autocomplete.length == 0) {
            dispatch('arrow', direction);
            return;
        }

        let currentElm: HTMLElement;
        let selectedElm: ChildNode;
        if (selectedResult == '') {
            let list = document.getElementById('autocomplete-list');
            if (direction == 'down') {
                selectedElm = list.firstChild;
            } else {
                selectedElm = list.lastChild;
            }
        } else {
            currentElm = document.getElementById(selectedResult);
            if (direction == 'up') {
                selectedElm = currentElm.previousSibling;
            } else if (direction == 'down') {
                selectedElm = currentElm.nextSibling;
            }
        }

        if (selectedElm != null) {
            selectResult((selectedElm as HTMLDivElement).id, false);
        } else {
            selectResult('', false);
        }
    }

    function searchButton() {
        if (autocomplete.length > 0) {
            selectResult(autocomplete[0].id, true);
            setFocus();
        }
    }

    function closeButton() {
        history.pushState({}, null, "/search");
        input.value = '';
        searchString = '';
        selectedResult = '';
        filterOn = false;
        autocomplete = [];
        dispatch('clear');
        setFocus();
    }

    function filterButton() {
        filterOn = !filterOn;
    }

    function handleFilter(e: MouseEvent) {
        filter = (e.target as HTMLInputElement).value;
        getAutocompletion();
        setFocus();
    }

    function handleMouseSelect(e: MouseEvent) {
        let elem = (e.target as HTMLDivElement);
        if (elem.id == '') {
            elem = (e.target as HTMLDivElement).parentElement as HTMLDivElement;
            if (elem.id == '') {
                elem = (elem as HTMLDivElement).parentElement as HTMLDivElement;
                if (elem.id == '') {
                    elem = (elem as HTMLDivElement).parentElement as HTMLDivElement;
                }
            }
        }

        filterOn = false;
        setFocus();
        selectResult(elem.id, true);
    }

    function handleKeyCommands(e: KeyboardEvent) {
        switch (e.code) {
            case 'Enter':
                e.preventDefault();
                filterOn = false;
                if (selectedResult != '') {
                    selectResult(selectedResult, true);
                } else {
                    searchButton();
                }
                break;
            case 'ArrowDown':
                e.preventDefault();
                selectResultKey('down');
                break;
            case 'ArrowUp':
                e.preventDefault();
                selectResultKey('up');
                break;
        }
    }

    function setFocus() {
        mobileResult = false;
        if (window.outerWidth > 768) {
            input.focus();
        }
    }
</script>

<div class="text-xl">
    <div class="mt-5 h-12 px-4 flex items-center bg-white">
        <span class="mr-3 cursor-pointer"><SearchLine on:click={searchButton}/></span>
        <input id="input" class="w-full flex items-center outline-none bg-white" bind:this={input}
               bind:value={searchString} on:keydown={handleKeyCommands}
               on:input={getAutocompletion} />
        <span class="w-2"></span>
        <span class="mr-1 cursor-pointer"><CloseLine on:click={closeButton}/></span>
        <span class="cursor-pointer">
            {#if filter == 'all'}
                <Filter2Line on:click={filterButton} />
                {:else}
                <Filter2Fill on:click={filterButton} />
                {/if}
        </span>
    </div>
</div>

{#if filterOn}
    <div class="flex flex-row py-2 px-2 bg-secondary">
        <div class="mr-6 form-check form-check-inline">
            <input type="radio" on:click={handleFilter} bind:group={filter} value="all" id="filter-all"
                   class="form-check-input form-check-input appearance-none rounded-full h-4 w-4 border border-gray-300 bg-white checked:bg-blue-600 checked:border-blue-600 focus:outline-none transition duration-200 mt-1 align-top bg-no-repeat bg-center bg-contain float-left mr-2 cursor-pointer">
            <label class="form-check-label inline-block text-gray-800"
                   for="filter-all">{$_('search.bar.filter.all')}</label>
        </div>
        <div class="mr-6 form-check form-check-inline">
            <input type="radio" on:click={handleFilter} bind:group={filter} value="sender" id="filter-sender"
                   class="form-check-input form-check-input appearance-none rounded-full h-4 w-4 border border-gray-300 bg-white checked:bg-blue-600 checked:border-blue-600 focus:outline-none transition duration-200 mt-1 align-top bg-no-repeat bg-center bg-contain float-left mr-2 cursor-pointer">
            <label class="form-check-label inline-block text-gray-800"
                   for="filter-sender">{$_('search.bar.filter.sender')}</label>
        </div>
        <div class="form-check form-check-inline">
            <input type="radio" on:click={handleFilter} bind:group={filter} value="receiver" id="filter-receiver"
                   class="form-check-input form-check-input appearance-none rounded-full h-4 w-4 border border-gray-300 bg-white checked:bg-blue-600 checked:border-blue-600 focus:outline-none transition duration-200 mt-1 align-top bg-no-repeat bg-center bg-contain float-left mr-2 cursor-pointer">
            <label class="form-check-label inline-block text-gray-800"
                   for="filter-receiver">{$_('search.bar.filter.receiver')}</label>
        </div>
    </div>
{/if}

{#if autocomplete.length > 0}
    <div id="autocomplete-list" class="flex flex-col py-2 px-2 bg-white border-t border-solid border-color">
        {#each autocomplete as pro}
            <div id={pro.id} on:click={handleMouseSelect}
                 class="flex z-10 py-1 px-2 hover:bg-primary cursor-pointer {selectedResult === pro.id ? 'bg-primary' : ''}">
                <div class="w-10/12">
                    <div>
                        <div>{pro.name}</div>
                        <div class="text-sm">{pro.name2}</div>
                    </div>
                </div>
                <div class="flex items-center justify-end w-2/12 px-1">
                    <div>
                        <img src={getProviderIcon(pro.providerID.toString())}
                             width="28px" height="28px" alt="{pro.providerID} icon" />
                    </div>
                </div>
            </div>
        {/each}
    </div>
{/if}

<style windi:preflights>
</style>
